import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from '@mui/material';
import { styled } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ToastMessageInput from '../../utils/ToastMessageInput';
import TemplateSelector from './TemplateSelector';
import type { AppDispatch } from '../../store';
import {
  selectTemplates,
  selectSelectedTemplateId,
  createTemplate,
  updateTemplate,
  removeTemplate,
  loadTemplates,
} from '../../store/reducers/templates';
import { hasPermission } from '../../lib/authz';
import { RootState } from '../../store/types/sharedTypes';

const ActionsRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
  margin-top: 8px;
`;

const getErrorMessage = (e: unknown) => {
  if (e instanceof Error) {
    return e.message;
  }
  if (typeof e === 'string') {
    return e;
  }
  return 'A error occurred';
};

const TemplateManagementPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const templates = useSelector(selectTemplates);
  const selectedTemplateId = useSelector(selectSelectedTemplateId);
  const selectedTemplate = useMemo(
    () => templates.find((tpl) => tpl._id === selectedTemplateId) ?? null,
    [templates, selectedTemplateId]
  );

  const { sessionUser } = useSelector((state: RootState) => state.general);
  const canWriteTemplate = hasPermission(sessionUser, 'template:writeTemplate');
  const allowDelete = !!selectedTemplate && canWriteTemplate;

  const [confirmOpen, setConfirmOpen] = useState(false);

  const [templateMode, setTemplateMode] = useState<'' | 'create' | 'update'>(
    ''
  );

  const [name, setName] = useState('');
  const [editorValue, setEditorValue] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState<{
    open: boolean;
    msg: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, msg: '', severity: 'success' });

  const showSelector = templateMode === 'update';
  const showForm = templateMode === 'create' || templateMode === 'update';

  useEffect(() => {
    if (templateMode === 'update' && selectedTemplate) {
      setName(selectedTemplate.name ?? '');
      setEditorValue(selectedTemplate.bodyMarkdown ?? '');
    } else if (templateMode === 'create') {
      setName('');
      setEditorValue('');
    } else if (!templateMode) {
      setName('');
      setEditorValue('');
    }
  }, [templateMode, selectedTemplateId, selectedTemplate]);

  const onDeleteClick = async () => {
    if (!selectedTemplate) return;

    try {
      setBusy(true);
      await dispatch(removeTemplate(selectedTemplate._id)).unwrap();
      await dispatch(loadTemplates()).unwrap();

      setSnack({
        open: true,
        msg: String(t('template.deleted')),
        severity: 'success',
      });

      setName('');
      setEditorValue('');
    } catch (e) {
      const message = getErrorMessage(e);

      setSnack({
        open: true,
        msg: message,
        severity: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const onPrimaryClick = async () => {
    if (!templateMode) return;

    try {
      setBusy(true);

      if (templateMode === 'update' && selectedTemplate) {
        await dispatch(
          updateTemplate({
            id: selectedTemplate._id,
            body: { name, bodyMarkdown: editorValue },
          })
        ).unwrap();

        await dispatch(loadTemplates()).unwrap();

        setSnack({
          open: true,
          msg: String(t('template.updated')),
          severity: 'success',
        });
      } else if (templateMode === 'create') {
        await dispatch(
          createTemplate({ name, bodyMarkdown: editorValue })
        ).unwrap();

        await dispatch(loadTemplates()).unwrap();

        setSnack({
          open: true,
          msg: String(t('template.created')),
          severity: 'success',
        });
      }
    } catch (e) {
      const message = getErrorMessage(e);

      setSnack({
        open: true,
        msg: message,
        severity: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const primaryButtonLabel =
    templateMode === 'update' && selectedTemplate
      ? String(t('save'))
      : String(t('create'));

  return (
    <Box sx={{ width: '100%', p: 2, boxSizing: 'border-box' }}>
      <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          title={String(t('template.header'))}
          subheader={String(t('template.subheader'))}
        />

        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ maxWidth: 800, width: '100%' }}>
            <TextField
              select
              fullWidth
              size="small"
              label={String(t('template.selectaction'))}
              value={templateMode}
              onChange={(e) =>
                setTemplateMode(e.target.value as '' | 'create' | 'update')
              }
            >
              <MenuItem value="create">
                {String(t('template.createTemplate'))}
              </MenuItem>
              <MenuItem value="update">
                {String(t('template.updateTemplate'))}
              </MenuItem>
            </TextField>
          </Box>

          {showForm && (
            <>
              {showSelector && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    width: '100%',
                    minWidth: 0,
                    maxWidth: 800,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <TemplateSelector
                      size="small"
                      onInsert={(rendered: string) => setEditorValue(rendered)}
                    />
                  </Box>

                  {allowDelete && (
                    <Box sx={{ ml: 1, flexShrink: 0 }}>
                      <Button
                        variant="contained"
                        color="error"
                        disabled={busy}
                        onClick={() => setConfirmOpen(true)}
                      >
                        {String(t('delete'))}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              <Box sx={{ maxWidth: 800, width: '100%' }}>
                <TextField
                  fullWidth
                  size="small"
                  label={String(t('template.name'))}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Box>

              <Box
                sx={{
                  mt: 1,
                  height: '45vh',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{ flex: 1, minHeight: 0, maxWidth: 800, width: '100%' }}
                >
                  <ToastMessageInput
                    value={editorValue}
                    onChange={(md: string) => setEditorValue(md)}
                    placeholder={String(t('template.editorPlaceholder'))}
                    height="100%"
                  />
                </Box>

                <Box sx={{ maxWidth: 800, width: '100%' }}>
                  <ActionsRow>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={busy || name.trim() === ''}
                      onClick={onPrimaryClick}
                    >
                      {busy ? (
                        <CircularProgress size={20} />
                      ) : (
                        primaryButtonLabel
                      )}
                    </Button>
                  </ActionsRow>
                </Box>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{String(t('template.confirmDelete'))}</DialogTitle>
        <DialogContent>{String(t('template.confirmDeleteBody'))}</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>
            {String(t('cancel'))}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={async () => {
              await onDeleteClick();
              setConfirmOpen(false);
            }}
          >
            {String(t('delete'))}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TemplateManagementPage;
